import { BannerType, PageType, TargetParams } from "./BannerContainer";
import Tracking from "../uidispatcherapi/Tracking";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import { Banner, BannerGroup } from "../stores/BannerStore";
import { FeatureSessionStore } from "./../uidispatcherapi/FeatureSessionStore";
import { sha256 } from "js-sha256";
import { memoize, extractHostname } from "./util";
import { getScreenType } from "./BannerUtil";

type SessionInfo = {
    store?: string;
    customerId?: string;
    isEmployee: boolean;
};

type Kpi = "impression" | "click" | "sponsored.click" | "impression.carousel";
const _impression = "impression";
const _click = "click";

function getBannerTargetLinkWithinMShop(targetLink?: string, linkType?: string): boolean {
    if (!targetLink) {
        return false;
    }
    if (linkType !== "PURE_LINK") {
        // must be some kind of relative link hence within MShop
        return true;
    }
    const targetHost = targetLink ? extractHostname(targetLink) : null;
    return targetHost === window.location.hostname;
}

const digestCache = memoize((data: string) => {
    return sha256(data);
});

function getSessionInfo(): SessionInfo {
    const customerId = SessionStore.getCustomerId();
    const hashedCustomerId = customerId ? digestCache(customerId) : undefined;
    return {
        customerId: hashedCustomerId,
        isEmployee: SessionStore.isEmployee(),
        store: SessionStore.getCustomerStore()
    };
}

function defaultTags(kpi: Kpi, pageType: PageType) {
    return { kpi: kpi, bannerPageType: pageType };
}

function sessionTags(sessionInfo: SessionInfo) {
    return {
        customerId: sessionInfo.customerId,
        store: sessionInfo.store
    };
}

function targetParamsTags(targetParams?: TargetParams) {
    return {
        customerTargetGroup: targetParams?.customerTargetGroup,
        customerCategoryGroup: targetParams?.customerCategoryGroup,
        customerType: targetParams?.customerType,
        categoryId: targetParams?.categoryId,
        categoryPathIds: targetParams?.categoryPathId?.join(","),
        storelistId: targetParams?.storelistId,
        isCarouselMember: targetParams?.isCarouselMember
    };
}

function getDefaultBannerTags(
    kpi: Kpi,
    banner: Banner,
    pageType: PageType,
    sessionInfo: SessionInfo,
    targetParams?: TargetParams
) {
    return {
        ...defaultTags(kpi, pageType),
        ...targetParamsTags(targetParams),
        ...sessionTags(sessionInfo),
        campaignId: banner.campaignId,
        campaignName: banner.campaignName,
        placementId: banner.placementId,
        placementName: banner.placementName,
        bannerType: banner.type,
        page: banner.page,
        orderType: banner.orderType
    };
}

function getDefaultBannerGroupTags(
    kpi: Kpi,
    banners: BannerGroup,
    pageType: PageType,
    sessionInfo: SessionInfo,
    targetParams?: TargetParams
) {
    return {
        ...defaultTags(kpi, pageType),
        ...targetParamsTags(targetParams),
        ...sessionTags(sessionInfo),
        bannerCount: banners.length,
        campaignId: banners[0].campaignId,
        campaignName: banners[0].campaignName
    };
}

const emitTrackingEvent = function(kpi: Kpi, sessionInfo: SessionInfo, tags: any) {
    const orderHabitFeatureToggle = FeatureSessionStore.getMembershipFor("ENABLE_ORDER_HABITS");

    if (orderHabitFeatureToggle === "B") {
        Object.assign(tags, {
            customerId: sessionInfo.customerId
        });
    }
    // if we are in an employee context, don't track banner kpis
    if (!sessionInfo.isEmployee) {
        Tracking.trackStructuredEvent("platform", "ui", "banner." + kpi, { tags }, null);
    }
};
const getBannerTags = function(
    kpi: Kpi,
    sessionInfo: SessionInfo,
    banner: Banner,
    pageType: PageType,
    targetParams?: TargetParams
) {
    const defaultBannerTags = getDefaultBannerTags(kpi, banner, pageType, sessionInfo, targetParams);
    return {
        ...defaultBannerTags,
        creativeId: banner.creativeId,
        targetLink: banner.link,
        targetLinkWithinMShop: getBannerTargetLinkWithinMShop(banner.link, banner.linkType),
        advertisement: banner.advertisement,
        screenType: getScreenType()
    };
};

const trackArticleBanner = function(
    kpi: Kpi,
    banner: Banner,
    pageType: PageType,
    bettyBundleIds: Array<string>,
    targetParams?: TargetParams
) {
    const sessionInfo = getSessionInfo();
    const defaultBannerTags = getDefaultBannerTags(kpi, banner, pageType, sessionInfo, targetParams);
    const tags = { ...defaultBannerTags, articleIds: bettyBundleIds };

    emitTrackingEvent(kpi, sessionInfo, tags);
};

const trackBannerImpression = function(banner: Banner, pageType: PageType, targetParams: TargetParams) {
    const sessionInfo = getSessionInfo();
    const kpi = _impression;
    const bannerTags = getBannerTags(kpi, sessionInfo, banner, pageType, targetParams);
    emitTrackingEvent(kpi, sessionInfo, bannerTags);
};

const trackBannerGroupImpression = function(banners: BannerGroup, pageType: PageType, targetParams?: TargetParams) {
    const sessionInfo = getSessionInfo();
    const kpi = "impression.carousel";
    const defaultBannerGroupTags = getDefaultBannerGroupTags(kpi, banners, pageType, sessionInfo, targetParams);
    emitTrackingEvent(kpi, sessionInfo, { ...defaultBannerGroupTags, screenType: getScreenType() });
};

const trackBannerClick = function(banner: Banner, pageType: PageType, targetParams?: TargetParams) {
    const sessionInfo = getSessionInfo();
    const kpi = _click;
    const bannerTags = getBannerTags(kpi, sessionInfo, banner, pageType, targetParams);
    emitTrackingEvent(kpi, sessionInfo, bannerTags);
};

const trackArticleBannerClick = function(
    banner: Banner,
    pageType: PageType,
    articleId: string,
    targetParams?: TargetParams
) {
    trackArticleBanner(_click, banner, pageType, [articleId], targetParams);
};

const trackArticleBannerSponsoredClick = (
    banner: Banner,
    pageType: PageType,
    articleId: string,
    targetParams?: TargetParams
) => trackArticleBanner("sponsored.click", banner, pageType, [articleId], targetParams);

const trackArticleBannerImpression = (
    banner: any,
    pageType: PageType,
    articleIds: Array<string>,
    targetParams?: TargetParams
) => trackArticleBanner("impression", banner, pageType, articleIds, targetParams);

const trackBannerError = function(pageType: PageType, bannerType: BannerType, targetParams?: TargetParams) {
    const tags = {
        kpi: "error",
        bannerType: bannerType,
        page: pageType,
        targetParams: JSON.stringify(targetParams || {})
    };
    Tracking.trackStructuredEvent("platform", "ui", "banner.error", { tags }, null);
};

const exported = {
    trackBannerImpression,
    trackBannerClick,
    trackArticleBannerClick,
    trackArticleBannerImpression,
    trackArticleBannerSponsoredClick,
    trackBannerError
};

export default exported;
export {
    trackBannerImpression,
    trackBannerClick,
    trackBannerGroupImpression,
    trackArticleBannerClick,
    trackArticleBannerImpression,
    trackArticleBannerSponsoredClick,
    trackBannerError
};
